import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme, themes } from '@/context/ThemeContext';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme, currentTheme } = useTheme();
  const [showThemeMenu, setShowThemeMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleName = (role) => {
    const roleNames = {
      'admin': 'Yönetici',
      'muhendis': 'Mühendis',
      'atolye': 'Atölye',
      'muhasebe': 'Muhasebe'
    };
    return roleNames[role] || role;
  };

  // Atölye kullanıcısı sadece Vinçler'i görebilir
  const isAtolyeOnly = user?.role === 'atolye';

  return (
    <div className="layout-container">
      <aside className="sidebar" style={{ background: currentTheme.sidebar }}>
        <div className="sidebar-header">
          <img 
            src="/logo.png" 
            alt="Doğan Vinç Logo" 
            style={{ 
              width: '100%',
              maxWidth: '200px',
              height: 'auto',
              marginBottom: '12px',
              objectFit: 'contain',
              background: 'white',
              padding: '8px',
              borderRadius: '8px'
            }} 
          />
          <p>ERP Yönetim Sistemi</p>
          {user && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '8px',
              fontSize: '13px'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>{user.full_name}</div>
              <div style={{ opacity: 0.8, fontSize: '12px' }}>{getRoleName(user.role)}</div>
            </div>
          )}
        </div>
        <nav>
          {/* Kontrol Paneli - Atölye hariç herkes */}
          {!isAtolyeOnly && (
            <NavLink to="/" end className="nav-link" data-testid="nav-dashboard">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Kontrol Paneli
            </NavLink>
          )}
          
          {/* Müşteriler - Atölye hariç herkes */}
          {!isAtolyeOnly && (
            <NavLink to="/musteriler" className="nav-link" data-testid="nav-customers">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Müşteriler
            </NavLink>
          )}
          
          {/* Siparişler - Atölye hariç herkes */}
          {!isAtolyeOnly && (
            <NavLink to="/siparisler" className="nav-link" data-testid="nav-orders">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Siparişler
            </NavLink>
          )}
          
          {/* Vinçler - Herkes görebilir */}
          <NavLink to="/vincler" className="nav-link" data-testid="nav-cranes">
            <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Vinçler
          </NavLink>
          
          {/* Teklifler - Atölye hariç herkes */}
          {!isAtolyeOnly && (
            <NavLink to="/teklifler" className="nav-link" data-testid="nav-proposals">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              Teklifler
            </NavLink>
          )}
          
          {/* Banka Hesapları - Sadece admin ve muhasebe */}
          {(user?.role === 'admin' || user?.role === 'muhasebe') && (
            <NavLink to="/banka-hesaplari" className="nav-link" data-testid="nav-banks">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              Banka Hesapları
            </NavLink>
          )}
          
          {/* AI Dosya Yükleme - Atölye hariç herkes */}
          {!isAtolyeOnly && (
            <NavLink to="/ai-yukleme" className="nav-link" data-testid="nav-ai">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              AI Dosya Yükleme
            </NavLink>
          )}
          
          {/* Kullanıcı Yönetimi - Sadece admin */}
          {user?.role === 'admin' && (
            <NavLink to="/kullanici-yonetimi" className="nav-link">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Kullanıcı Yönetimi
            </NavLink>
          )}
        </nav>
        
        {/* Tema Seçici */}
        <div style={{
          position: 'absolute',
          bottom: '80px',
          left: '20px',
          right: '20px'
        }}>
          <button
            onClick={() => setShowThemeMenu(!showThemeMenu)}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '13px'
            }}
          >
            <span>{currentTheme.icon}</span>
            <span>Tema: {currentTheme.name}</span>
          </button>
          
          {showThemeMenu && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              right: 0,
              background: '#fff',
              borderRadius: '8px',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              marginBottom: '5px',
              zIndex: 1000
            }}>
              {Object.entries(themes).map(([key, t]) => (
                <button
                  key={key}
                  onClick={() => {
                    setTheme(key);
                    setShowThemeMenu(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    border: 'none',
                    background: theme === key ? currentTheme.primary : 'transparent',
                    color: theme === key ? 'white' : '#333',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}
                >
                  <span>{t.icon}</span>
                  <span>{t.name}</span>
                  {theme === key && <span style={{ marginLeft: 'auto' }}>✔</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Çıkış Butonu */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          right: '20px'
        }}>
          <button
            onClick={handleLogout}
            className="nav-link"
            style={{
              width: '100%',
              background: 'rgba(239, 68, 68, 0.1)',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left'
            }}
            data-testid="logout-button"
          >
            <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Çıkış Yap
          </button>
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
